'use client';

import {
  useReviewCreate,
  useReviewUpdate,
  type WebshopReviewImage,
  WebshopReviewQueries,
} from '@cldsystems/ws-api-client';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useMemo, useState } from 'react';
import { toast } from 'sonner';
import MediaDragAndDrop from '@/components/studio/media-drag-and-drop/media-drag-and-drop';
import {
  Button,
  Divider,
  Input,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Textarea,
} from '@/components/ui';
import useReviewDeleteImageMutation from '@/hooks/use-review-delete-image-mutation';
import useReviewUploadImageMutation from '@/hooks/use-review-upload-image-mutation';
import { useTranslation } from '@/providers/app-provider/app-provider';
import { useKeycloak } from '@/providers/keycloak-provider';
import RatingRadioGroup from '../rating-radio-group/rating-radio-group';

const MAX_IMAGES = 5;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const ACCEPTED_IMAGE_TYPES_LABEL = 'JPG, PNG, WEBP';

export type EditTarget = {
  id: string;
  version: number;
  /**
   * The locale-specific product the review is already filed against. Reviews are
   * read across every locale for a SKU, so the one being edited may not belong to
   * the locale on screen — sending the viewed id would move it.
   */
  productId: string;
  initialValues: {
    rating: number;
    title: string;
    message: string;
    images: WebshopReviewImage[];
  };
};

type ModalReviewContentProps = {
  productId: string;
  editTarget?: EditTarget;
  onClose: () => void;
};

const ModalReviewContent: FC<ModalReviewContentProps> = ({ productId, editTarget, onClose }) => {
  const { t, tFormat } = useTranslation();
  const queryClient = useQueryClient();
  const reviewCreate = useReviewCreate();
  const reviewUpdate = useReviewUpdate();
  const reviewUploadImage = useReviewUploadImageMutation();
  const reviewDeleteImage = useReviewDeleteImageMutation();
  const { isAuthenticated } = useKeycloak();
  const isEdit = !!editTarget;

  const initialImages = useMemo(
    () => editTarget?.initialValues.images ?? [],
    [editTarget?.initialValues.images],
  );
  const urlToImageId = useMemo(() => {
    const map = new Map<string, string>();
    initialImages.forEach(img => map.set(img.url, img.id));
    return map;
  }, [initialImages]);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [rating, setRating] = useState(editTarget ? String(editTarget.initialValues.rating) : '5');
  const [title, setTitle] = useState(editTarget?.initialValues.title ?? '');
  const [message, setMessage] = useState(editTarget?.initialValues.message ?? '');
  const [imageUrls, setImageUrls] = useState<string[]>(initialImages.map(img => img.url));
  const [pendingFiles, setPendingFiles] = useState<Record<string, File>>({});

  const invalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: [WebshopReviewQueries.GetReviews] });
    queryClient.invalidateQueries({ queryKey: [WebshopReviewQueries.GetMyReviews] });
    queryClient.invalidateQueries({ queryKey: [WebshopReviewQueries.GetList] });
    queryClient.invalidateQueries({ queryKey: [WebshopReviewQueries.GetById] });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const remainingExistingUrls = new Set(imageUrls.filter(url => urlToImageId.has(url)));
    const removedImageIds = initialImages
      .filter(img => !remainingExistingUrls.has(img.url))
      .map(img => img.id);
    const newFiles = imageUrls
      .filter(url => !urlToImageId.has(url))
      .map(url => pendingFiles[url])
      .filter((f): f is File => !!f);

    try {
      let reviewId: string;
      let currentVersion: number;

      if (editTarget) {
        reviewId = editTarget.id;
        currentVersion = editTarget.version;
        const initial = editTarget.initialValues;
        const textChanged =
          Number(rating) !== initial.rating ||
          title !== initial.title ||
          message !== initial.message;

        if (textChanged) {
          await reviewUpdate.mutateAsync({
            params: { id: reviewId, version: String(currentVersion) },
            payload: {
              productId: editTarget.productId,
              ranking: Number(rating),
              title,
              message,
            },
          });
          currentVersion += 1;
        }
      } else {
        const newId = crypto.randomUUID();
        await reviewCreate.mutateAsync({
          payload: {
            id: newId,
            productId,
            ranking: Number(rating),
            title,
            message,
          },
        });
        reviewId = newId;
        currentVersion = 1;
      }

      try {
        for (const imageId of removedImageIds) {
          await reviewDeleteImage.mutateAsync({
            id: reviewId,
            version: currentVersion,
            imageId,
          });
          currentVersion += 1;
        }

        if (newFiles.length > 0) {
          await reviewUploadImage.mutateAsync({
            id: reviewId,
            version: currentVersion,
            files: newFiles,
          });
        }
      } catch {
        toast.error(t?.productDetail.addReviewModal.errorGeneric);
      }

      setTimeout(invalidateQueries, 1000);
      toast.success(
        isEdit
          ? t?.productDetail.addReviewModal.successUpdate
          : t?.productDetail.addReviewModal.successCreate,
      );
      onClose();
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : t?.productDetail.addReviewModal.errorGeneric;
      toast.error(message);
    }
  };

  const isPending =
    reviewCreate.isPending ||
    reviewUpdate.isPending ||
    reviewUploadImage.isPending ||
    reviewDeleteImage.isPending;

  return (
    <ModalContent>
      <ModalHeader className="flex-col pt-8">
        <h1 className="text-large font-semibold">{t?.productDetail.addReviewModal.title}</h1>
        <p className="text-small text-default-700 font-normal">
          {t?.productDetail.addReviewModal.subTitle}
        </p>
      </ModalHeader>
      <ModalBody className="pb-8">
        <form id="review-form" className="flex flex-col gap-6" onSubmit={handleSubmit}>
          {!isAuthenticated && !isEdit && (
            <>
              <Input
                isRequired
                label={t?.productDetail.addReviewModal.nameLabel}
                placeholder={t?.productDetail.addReviewModal.namePlaceholder}
                value={name}
                onValueChange={setName}
                labelPlacement="outside"
              />
              <Input
                isRequired
                label={t?.productDetail.addReviewModal.emailLabel}
                placeholder={t?.productDetail.addReviewModal.emailPlaceholder}
                type="email"
                value={email}
                onValueChange={setEmail}
                labelPlacement="outside"
              />
              <Divider />
            </>
          )}
          <RatingRadioGroup
            hideStarsText
            className="flex-col-reverse items-start"
            color="warning"
            label={
              <span className="text-small">{t?.productDetail.addReviewModal.ratingLabel}</span>
            }
            value={rating}
            onValueChange={setRating}
          />
          <Input
            isRequired
            label={t?.productDetail.addReviewModal.titleLabel}
            placeholder={t?.productDetail.addReviewModal.titlePlaceholder}
            value={title}
            onValueChange={setTitle}
            labelPlacement="outside"
          />
          <Textarea
            disableAutosize
            isRequired
            classNames={{
              input: 'h-32 resize-y !transition-none',
            }}
            label={t?.productDetail.addReviewModal.commentLabel}
            placeholder={t?.productDetail.addReviewModal.commentPlaceholder}
            value={message}
            onValueChange={setMessage}
            labelPlacement="outside"
          />

          <MediaDragAndDrop
            allowGallery={false}
            maxFiles={MAX_IMAGES}
            acceptedTypes={ACCEPTED_IMAGE_TYPES}
            unsupportedFormatMessage={tFormat(
              t?.productDetail.addReviewModal.imagesUnsupportedFormat,
              { formats: ACCEPTED_IMAGE_TYPES_LABEL },
            )}
            title={t?.productDetail.addReviewModal.imagesLabel}
            description={tFormat(t?.productDetail.addReviewModal.imagesMaxHint, {
              max: MAX_IMAGES,
            })}
            buttonText={t?.productDetail.addReviewModal.imagesAdd}
            value={imageUrls}
            onChange={setImageUrls}
            onFilesAdded={entries => {
              setPendingFiles(prev => {
                const next = { ...prev };
                entries.forEach(({ file, url }) => {
                  next[url] = file;
                });
                return next;
              });
            }}
            disabled={isPending}
          />
        </form>
      </ModalBody>
      <ModalFooter>
        <Button isDisabled={isPending} variant="light" onClick={onClose}>
          {t?.productDetail.addReviewModal.close}
        </Button>
        <Button color="primary" form="review-form" isLoading={isPending} type="submit">
          {isEdit
            ? t?.productDetail.addReviewModal.saveChanges
            : t?.productDetail.addReviewModal.sendSubmit}
        </Button>
      </ModalFooter>
    </ModalContent>
  );
};

export default ModalReviewContent;
